import { trackEvent } from "../analytics/analyticsClient";
// Scroll reveals — .lp-js gates the hidden state so no-JS visitors (and
// crawlers) always see content. Reduced-motion users get everything
// visible immediately.
const lp = document.querySelector(".lp");
if (lp) {
	const els = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
	const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	if (!reduce && "IntersectionObserver" in window && els.length > 0) {
		// Already added by the inline script above, before first paint. Kept as a
		// no-op safety net for the case where that script did not run.
		lp.classList.add("lp-js");
		// Disarms that script's fail-open watchdog — see the note beside it.
		// Must be set here, where the observer is about to exist, not earlier.
		lp.setAttribute("data-reveal-ready", "");
		const io = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					entry.target.classList.add("is-in");
					io.unobserve(entry.target);
				}
			}
		}, {
			rootMargin: "0px 0px -8% 0px",
			threshold: .1
		});
		els.forEach((el) => io.observe(el));
	}
}
// Sticky header earns its edge only once content scrolls beneath it
const header = document.querySelector(".lp-header");
if (header) {
	const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
	onScroll();
	window.addEventListener("scroll", onScroll, { passive: true });
}
// Theme control. The initial resolution already happened in BaseLayout's
// inline head script — this only handles CHANGES, so nothing here is on the
// critical path and a slow module load can never cause a flash.
//
// Two states only, and `prefers-color-scheme` is not consulted anywhere:
// light is the default and dark is opt-in. That means the stored value and
// the `data-theme` attribute are always identical, so there is no separate
// preference attribute and no OS-change listener to keep in sync.
const THEME_KEY = "qt.theme.preference";
const themeRoot = document.documentElement;
// aria-checked is the one piece of switch state CSS cannot express, so it is
// synced here on load. The VISUAL state was already correct before this ran.
const syncSwitches = () => {
	const isDark = themeRoot.getAttribute("data-theme") === "dark";
	document.querySelectorAll<HTMLElement>("[data-theme-switch]").forEach((el) => el.setAttribute("aria-checked", String(isDark)));
};
syncSwitches();
let themeAnimTimer: ReturnType<typeof setTimeout> | undefined;
document.addEventListener("click", (event) => {
	const target = event.target as HTMLElement | null;
	if (!target?.closest("[data-theme-switch]")) return;
	const next = themeRoot.getAttribute("data-theme") === "dark" ? "light" : "dark";
	try {
		localStorage.setItem(THEME_KEY, next);
	} catch {}
	// The cross-fade class is added ONLY around a deliberate switch and torn
	// down after. Left on permanently it would animate the first paint and
	// park the whole tree on composited layers; see `.lp-theme-anim`.
	themeRoot.classList.add("lp-theme-anim");
	clearTimeout(themeAnimTimer);
	themeAnimTimer = setTimeout(() => themeRoot.classList.remove("lp-theme-anim"), 340);
	themeRoot.setAttribute("data-theme", next);
	themeRoot.style.colorScheme = next;
	syncSwitches();
});
// CTA click tracking without hydrating a React island for static anchors.
document.addEventListener("click", (event) => {
	const target = event.target as HTMLElement | null;
	const cta = target?.closest<HTMLElement>("[data-cta]");
	if (!cta) return;
	trackEvent("website_cta_clicked", {
		cta_id: cta.dataset.cta ?? "landing_cta",
		location: "landing_v2",
		destination: cta.getAttribute("href") ?? ""
	});
});